<template>
  <div class="flex flex-row h-full">
    <div v-if="totalNumber > 1 && numberOfPreviews > 0" class="cursor-pointer flex flex-col justify-center prev-next-container" @click="previous()">
      <Chevron class="prev-button" />
    </div>
    <div class="grow flex flex-col">
      <div class="grow relative flex justify-stretch">
        <ImageComp
          alt=""
          :file="imageBlobs[currentIndex]"
          :enlargeable="enlargeable"
          :busy="unref(loadingImages[currentIndex])"
          :disabled="disabled"
          :rotation-enabled="enableRotation"
          :file-entity="enableRotation ? props.images[currentIndex]?.file : undefined"
          :initial-rotation="enableRotation ? rotations[currentIndex] : undefined"
          @save:rotation="saveRotation(currentIndex, $event)"
        />
      </div>
      <div v-if="totalNumber > 1 && numberOfPreviews > 0" class="preview-image-wrapper">
        <div
          v-for="index in previewImagesIndices"
          :key="index"
          class="preview-image"
          @click="jumpTo(index)"
        >
          <ImageComp
            alt=""
            :file="imageBlobs[index]"
            :busy="unref(loadingImages[index])"
            :initial-rotation="enableRotation ? rotations[index] : undefined"
          />
        </div>
      </div>
    </div>
    <div v-if="totalNumber > 1 && numberOfPreviews > 0" class="cursor-pointer flex flex-col justify-center prev-next-container" @click="next()">
      <Chevron class="next-button" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  computed, type Ref, ref, unref, watch,
} from 'vue';
import Chevron from '@images/chevron.svg?component';
import ImageComp from '@utils/Image.vue';
import { useBlob } from '@/hooks/file';
import type { WbFile } from '@/entities/Entities';

interface Image {
  file: WbFile;
  title?: string;
}

interface Props {
  images: Image[];
  enlargeable?: boolean;
  numberOfPreviews?: number;
  disabled?: boolean;
  enableRotation?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  numberOfPreviews: 4,
});

const currentIndex = ref(0);

const totalNumber = computed(() => props.images.length);

const loadingImages = ref<Ref<boolean>[]>([]);

const blobRefs = ref<Ref<Blob|undefined>[]>([]);

const rotations = ref<Record<number, number>>([]);

const loadImages = (newValue: Image[]) => {
  blobRefs.value = [];
  rotations.value = [];
  currentIndex.value = 0;
  newValue.forEach((el: Image) => {
    const { blob: srcBlob, loading } = useBlob(computed(() => `/files/${el?.file?.id}/content`));
    blobRefs.value.push(srcBlob);
    loadingImages.value.push(loading);
  });
};

loadImages(props.images);

watch(() => props.images, (newValue) => {
  loadImages(newValue);
});

const imageBlobs = computed(() => {
  const out: (Blob|undefined)[] = [];

  blobRefs.value.forEach((el: Ref<Blob | undefined>) => {
    out.push(unref(el));
  });
  return out;
});

const previewImagesIndices = computed(() => {
  const out: number[] = [];
  for (let i = 0; i < Math.min(totalNumber.value - 1, props.numberOfPreviews); ++i) {
    out.push((currentIndex.value + i + 1) % totalNumber.value);
  }
  return out;
});

function previous() {
  if (!props.disabled) {
    if (currentIndex.value > 0) {
      currentIndex.value--;
    } else {
      currentIndex.value = totalNumber.value - 1;
    }
  }
}

function next() {
  if (!props.disabled) {
    if (currentIndex.value < totalNumber.value - 1) {
      currentIndex.value++;
    } else {
      currentIndex.value = 0;
    }
  }
}

function jumpTo(index: number) {
  if (!props.disabled) {
    currentIndex.value = index;
  }
}

const saveRotation = (index: number, rotation: number) => {
  rotations.value[index] = ((rotations.value[index] ?? 0) + rotation) % 360;
}
</script>

<style lang="scss" scoped>
::v-deep(img) {
  position: absolute;
  object-fit: contain;
  height: 100%;
  user-select: none;
  cursor: pointer;
}
.prev-button {
  @apply text-red;
  height: 50px;
  transform: rotate(90deg);
}
.next-button {
  @apply text-red;
  height: 50px;
  transform: rotate(-90deg);
}
.prev-next-container {
  user-select: none;
}
.prev-next-container:hover {
  .prev-button, .next-button{
    @apply text-red-130;
  }
}

.preview-image-wrapper {
  @apply flex justify-between pt-4;
  max-height: 20%;
}

.preview-image {
  position:relative;
  height: 10rem;
  max-height: 100%;
  width: 10rem;
}
</style>
